import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faChevronDown, faTrash, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useMiniERPEDI } from '../hooks/useMiniERPEDI';

// Usage:
//   <MiniERPContactPicker
//     contactType="Customer"                      // or "Business"
//     currentName={form.customerName}
//     currentMobile={form.customerMobile}          // Customer contacts
//     currentCode={form.counterpartyCode}          // Business contacts
//     onPick={(contact) => setForm(f => ({ ...f, customerName: contact.name, customerMobile: contact.mobile }))}
//   />
const MiniERPContactPicker = ({ contactType, currentName, currentMobile, currentCode, onPick }) => {
  const { fetchContacts, createContact, deleteContact } = useMiniERPEDI();
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [whatsappInput, setWhatsappInput] = useState('');
  const [justSaved, setJustSaved] = useState(false);
  const wrapRef = useRef(null);

  const load = async () => {
    try {
      const res = await fetchContacts(contactType);
      setContacts(res?.data || []);
    } catch (err) {
      // Favouriting is a nicety — if the EDI routes aren't reachable yet
      // (e.g. not mounted on the server) this should never crash the page,
      // just leave the favourites list empty.
      console.warn('[MiniERP Contacts] failed to load:', err.message || err);
      setContacts([]);
    }
  };

  useEffect(() => { load(); }, [contactType]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setShowSaveForm(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const canSave = contactType === 'Business' ? !!currentCode : !!currentName;

  const openSaveForm = () => {
    if (!canSave) return;
    setWhatsappInput(currentMobile || '');
    setShowSaveForm(true);
    setOpen(false);
  };

  const handleSave = async () => {
    try {
      await createContact({
        name: currentName || currentCode,
        mobile: currentMobile || '',
        whatsappNumber: whatsappInput.replace(/[^\d+]/g, ''),
        contactType,
        businessUserCode: contactType === 'Business' ? currentCode : '',
      });
      await load();
      setShowSaveForm(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1500);
    } catch (err) {
      // silent — favouriting is a nicety, not worth a blocking error banner
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await deleteContact(id);
      await load();
    } catch (err) { /* ignore */ }
  };

  return (
    <div className="relative inline-flex gap-1" ref={wrapRef}>
      <button
        type="button"
        onClick={openSaveForm}
        disabled={!canSave}
        className={`w-fit px-1.5 py-1.5 rounded-lg text-xs transition-colors disabled:opacity-30 ${
          justSaved ? 'bg-amber-500 text-white' : 'bg-gray-600 text-gray-300 hover:bg-amber-600 hover:text-white'
        }`}
        title="Save as favourite"
      >
        <FontAwesomeIcon icon={faStar} />
      </button>

      <button
        type="button"
        onClick={() => { setOpen((o) => !o); setShowSaveForm(false); }}
        className="w-fit px-1.5 py-1.5 rounded-lg text-xs bg-gray-600 text-gray-300 hover:bg-cyan-600 hover:text-white transition-colors"
        title="Pick from favourites"
      >
        <FontAwesomeIcon icon={faChevronDown} />
      </button>

      {showSaveForm && (
        <div className="absolute right-0 top-9 z-20 w-64 bg-gray-800 border border-gray-600 rounded-xl shadow-xl p-3">
          <p className="text-white text-xs font-medium mb-2">Save "{currentName || currentCode}" as favourite</p>
          <label className="text-gray-400 text-[10px] block mb-1">WhatsApp number (optional — speeds up OTP sharing later)</label>
          <input
            type="tel"
            value={whatsappInput}
            onChange={(e) => setWhatsappInput(e.target.value.replace(/[^\d+ ]/g, ''))}
            placeholder="e.g. 9876543210"
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-xs mb-2"
          />
          <div className="flex gap-2">
            <button type="button" onClick={() => setShowSaveForm(false)} className="flex-1 w-fit px-2 py-1 border border-gray-600 rounded-lg text-xs hover:bg-gray-700">
              Cancel
            </button>
            <button type="button" onClick={handleSave} className="flex-1 w-fit px-2 py-1 bg-cyan-600 hover:bg-cyan-500 rounded-lg text-xs font-medium flex items-center justify-center gap-1">
              <FontAwesomeIcon icon={faCheck} /> Save
            </button>
          </div>
        </div>
      )}

      {open && (
        <div className="absolute right-0 top-9 z-20 w-64 bg-gray-800 border border-gray-600 rounded-xl shadow-xl max-h-56 overflow-y-auto">
          {contacts.length === 0 && (
            <p className="text-gray-500 text-xs p-3">No favourites saved yet.</p>
          )}
          {contacts.map((c) => (
            <div
              key={c._id}
              onClick={() => { onPick(c); setOpen(false); }}
              className="flex justify-between items-center px-3 py-2 hover:bg-gray-700 cursor-pointer text-xs border-b border-gray-700 last:border-b-0"
            >
              <div>
                <div className="text-white font-medium">{c.name}</div>
                <div className="text-gray-500 font-mono">{contactType === 'Business' ? c.businessUserCode : c.mobile}</div>
              </div>
              <button onClick={(e) => handleDelete(c._id, e)} className="text-gray-500 hover:text-rose-400 w-fit px-1 py-1" title="Remove favourite">
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MiniERPContactPicker;
